import { useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useMobileTitleOverride } from "../hooks/topBar/useMobileTitleOverride";
import { useMobileTrailingOverride } from "../hooks/topBar/useMobileTrailingOverride";
import type { MobileTopBarProps } from "../types";
import { shouldDeferDefaultTopBar } from "../utils/shouldDeferDefaultTopBar";
import { MobileDefaultTopBar } from "./MobileDefaultTopBar";

/**
 * Context-aware TopBar that shows either:
 * - A page-specific title (e.g. "Notifications", "Messages") when a
 *   title override is set via TitleOverrideContext, or
 * - The community name + logo with community switcher tap target (default).
 *
 * The trailing slot is fully contextual: it renders whatever the active
 * page registers via `setTrailingOverride` (e.g. the shared
 * CreateTrailingActionRegistrar's create button on Feed/Events/Spaces,
 * NotificationsTrailingActions on notifications, a "back to courses"
 * button on a course space) and is empty otherwise - there's no default.
 */
export const MobileTopBar = ({
  avatarName,
  avatarUrl,
  communityName,
  logoUrl,
  onTitleClick,
}: MobileTopBarProps) => {
  const pathname = useLocation({ select: location => location.pathname });
  const [defaultReadyPathname, setDefaultReadyPathname] = useState<
    string | null
  >(null);
  const { titleOverride } = useMobileTitleOverride();
  const { trailingOverride } = useMobileTrailingOverride();
  const hasTitleOverride = titleOverride != null;
  const shouldDeferDefault = shouldDeferDefaultTopBar(pathname);
  const isDefaultReady =
    !shouldDeferDefault || defaultReadyPathname === pathname;

  useEffect(() => {
    if (isDefaultReady) return undefined;

    const animationFrameId = requestAnimationFrame(() => {
      setDefaultReadyPathname(pathname);
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [isDefaultReady, pathname]);

  if (!hasTitleOverride && !isDefaultReady) {
    return (
      <MobileDefaultTopBar
        avatarName={avatarName}
        avatarUrl={avatarUrl}
        communityName={communityName}
        logoUrl={logoUrl}
        title={communityName}
        isHidden
      />
    );
  }

  return (
    <MobileDefaultTopBar
      avatarName={avatarName}
      avatarUrl={avatarUrl}
      communityName={communityName}
      // A page title override replaces the community branding
      logoUrl={hasTitleOverride ? undefined : logoUrl}
      title={titleOverride ?? communityName}
      onTitleClick={hasTitleOverride ? undefined : onTitleClick}
      trailing={trailingOverride}
    />
  );
};
